import React, { useState, useEffect } from "react";
import { useAuth, getAuthToken } from "../context/AuthContext";
import {
  CalendarCheck,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Users,
  Layers,
  Calendar,
  Filter,
  RefreshCw,
  Download,
  FileSpreadsheet,
  Save,
  Clock,
  Search,
  Check,
  X,
  TrendingUp,
  ShieldAlert,
  ChevronRight,
  ShieldCheck,
  UserCheck
} from "lucide-react";
import { StudentProfileModal } from "../components/StudentProfileModal";

export const AttendancePage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "principal";

  const [activeSubTab, setActiveSubTab] = useState<
    "mark" | "history" | "class-analysis" | "monthly-analysis" | "low-attendance"
  >("mark");

  // Masters
  const [boards, setBoards] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [sessions, setSessions] = useState<any[]>([]);

  // Filter Selection for Mark Attendance
  const [selectedBoard, setSelectedBoard] = useState<string>("");
  const [selectedClass, setSelectedClass] = useState<string>("");
  const [selectedSection, setSelectedSection] = useState<string>("A");
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toISOString().split("T")[0]
  );

  // Mark Attendance Sheet State
  const [sheetStudents, setSheetStudents] = useState<any[]>([]);
  const [sheetLoaded, setSheetLoaded] = useState<boolean>(false);
  const [isRecorded, setIsRecorded] = useState<boolean>(false);
  const [savingAttendance, setSavingAttendance] = useState<boolean>(false);
  const [saveSuccessMsg, setSaveSuccessMsg] = useState<string>("");
  const [saveErrorMsg, setSaveErrorMsg] = useState<string>("");

  // Summary KPIs State
  const [summary, setSummary] = useState<any>(null);

  // History State
  const [history, setHistory] = useState<any[]>([]);
  const [historyFilterDate, setHistoryFilterDate] = useState<string>("");
  const [historyFilterBoard, setHistoryFilterBoard] = useState<string>("");
  const [historyFilterClass, setHistoryFilterClass] = useState<string>("");
  const [historyFilterSection, setHistoryFilterSection] = useState<string>("");

  // Analysis State
  const [classAnalysis, setClassAnalysis] = useState<any[]>([]);
  const [monthlyAnalysis, setMonthlyAnalysis] = useState<any[]>([]);

  // Low Attendance State
  const [lowAttendanceList, setLowAttendanceList] = useState<any[]>([]);
  const [lowThreshold, setLowThreshold] = useState<number>(75);
  const [lowPriorityFilter, setLowPriorityFilter] = useState<string>("ALL");

  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");

  // Student Profile Modal
  const [selectedStudentForProfile, setSelectedStudentForProfile] = useState<any | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false);

  const defaultClasses = [
    { id: 3, name: "Class 6" },
    { id: 4, name: "Class 7" },
    { id: 5, name: "Class 8" },
    { id: 6, name: "Class 9" },
    { id: 1, name: "Class 10" },
    { id: 7, name: "Class 11" },
    { id: 2, name: "Class 12" },
  ];

  const defaultBoards = [
    { id: 1, name: "Haryana Board of School Education", short_name: "HBSE" },
    { id: 2, name: "Central Board of Secondary Education", short_name: "CBSE" },
  ];

  // Load Initial Master Data
  useEffect(() => {
    const token = getAuthToken();

    fetch("/api/classes")
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.classes) && d.classes.length > 0) {
          setClasses(d.classes);
          setSelectedClass((prev) => prev || String(d.classes[0].id));
        }
      })
      .catch(() => {});

    fetch("/api/boards")
      .then((r) => r.json())
      .then((d) => {
        if (d.success && Array.isArray(d.boards) && d.boards.length > 0) {
          setBoards(d.boards);
          setSelectedBoard((prev) => prev || String(d.boards[0].id));
        }
      })
      .catch(() => {});

    fetch("/api/dashboard/stats", {
      headers: { Authorization: `Bearer ${token}` }
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success && d.stats) {
          if (d.stats.boards?.length > 0) setBoards(d.stats.boards);
          if (d.stats.classes?.length > 0) setClasses(d.stats.classes);
          setSessions(d.stats.academic_sessions || []);
          if (d.stats.boards?.length > 0 && !selectedBoard) {
            setSelectedBoard(String(d.stats.boards[0].id));
          }
          if (d.stats.classes?.length > 0 && !selectedClass) {
            setSelectedClass(String(d.stats.classes[0].id));
          }
        }
      })
      .catch(() => {});
  }, []);

  // Fetch KPI Summary
  const fetchSummary = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch("/api/attendance/summary", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSummary(data.summary);
      }
    } catch (err) {}
  };

  useEffect(() => {
    fetchSummary();
  }, []);

  // Fetch Attendance Sheet for current Mark selection
  const fetchSheet = async () => {
    if (!selectedBoard || !selectedClass || !selectedSection || !selectedDate) return;
    setLoading(true);
    setSaveSuccessMsg("");
    setSaveErrorMsg("");
    try {
      const token = getAuthToken();
      const qs = new URLSearchParams({
        board_id: selectedBoard,
        class_id: selectedClass,
        section: selectedSection,
        attendance_date: selectedDate
      }).toString();

      const res = await fetch(`/api/attendance/sheet?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setSheetStudents(data.students || []);
        setIsRecorded(data.is_recorded || false);
        setSheetLoaded(true);
      } else {
        setSaveErrorMsg(data.message || "Failed to load class attendance sheet.");
        setSheetStudents([]);
        setSheetLoaded(false);
      }
    } catch (err: any) {
      setSaveErrorMsg("Network error loading sheet.");
      setSheetStudents([]);
      setSheetLoaded(false);
    } finally {
      setLoading(false);
    }
  };

  // Fetch whenever Mark filters change
  useEffect(() => {
    if (activeSubTab === "mark") {
      fetchSheet();
    }
  }, [selectedBoard, selectedClass, selectedSection, selectedDate, activeSubTab]);

  // Fetch History
  const fetchHistory = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const qs = new URLSearchParams();
      if (historyFilterDate) qs.set("date", historyFilterDate);
      if (historyFilterBoard) qs.set("board_id", historyFilterBoard);
      if (historyFilterClass) qs.set("class_id", historyFilterClass);
      if (historyFilterSection) qs.set("section", historyFilterSection);

      const res = await fetch(`/api/attendance/history?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setHistory(data.history || []);
      }
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    if (activeSubTab === "history") {
      fetchHistory();
    }
  }, [historyFilterDate, historyFilterBoard, historyFilterClass, historyFilterSection, activeSubTab]);

  // Fetch Class Analysis
  const fetchClassAnalysis = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch("/api/attendance/class-analysis", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setClassAnalysis(data.classes || []);
      }
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    if (activeSubTab === "class-analysis") {
      fetchClassAnalysis();
    }
  }, [activeSubTab]);

  // Fetch Monthly Analysis
  const fetchMonthlyAnalysis = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await fetch("/api/attendance/monthly-analysis", {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setMonthlyAnalysis(data.months || []);
      }
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    if (activeSubTab === "monthly-analysis") {
      fetchMonthlyAnalysis();
    }
  }, [activeSubTab]);

  // Fetch Low Attendance
  const fetchLowAttendance = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const qs = new URLSearchParams({
        threshold: String(lowThreshold),
        priority: lowPriorityFilter
      }).toString();

      const res = await fetch(`/api/attendance/low-attendance?${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setLowAttendanceList(data.low_attendance_students || []);
      }
    } catch (err) {}
    setLoading(false);
  };

  useEffect(() => {
    if (activeSubTab === "low-attendance") {
      fetchLowAttendance();
    }
  }, [lowThreshold, lowPriorityFilter, activeSubTab]);

  // Bulk Actions
  const handleBulkStatus = (newStatus: "present" | "absent") => {
    setSheetStudents((prev) =>
      prev.map((s) => ({
        ...s,
        status: newStatus
      }))
    );
  };

  // Individual Status Toggle
  const handleToggleStatus = (studentId: number, status: "present" | "absent") => {
    setSheetStudents((prev) =>
      prev.map((s) => (s.student_id === studentId ? { ...s, status } : s))
    );
  };

  // Save Attendance
  const handleSaveAttendance = async () => {
    setSavingAttendance(true);
    setSaveSuccessMsg("");
    setSaveErrorMsg("");

    try {
      const token = getAuthToken();
      const entries = sheetStudents.map((s) => ({
        student_id: s.student_id,
        status: s.status,
        remarks: s.remarks || null
      }));

      const res = await fetch("/api/attendance/save", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          board_id: Number(selectedBoard),
          class_id: Number(selectedClass),
          section: selectedSection,
          attendance_date: selectedDate,
          entries
        })
      });

      const data = await res.json();
      if (data.success) {
        setSaveSuccessMsg(
          `Attendance successfully ${isRecorded ? "updated" : "recorded"} (${data.present_count} Present, ${data.absent_count} Absent).`
        );
        setIsRecorded(true);
        fetchSummary();
      } else {
        setSaveErrorMsg(data.message || "Failed to save attendance.");
      }
    } catch (err: any) {
      setSaveErrorMsg("Server error saving attendance.");
    } finally {
      setSavingAttendance(false);
    }
  };

  // Export
  const handleExport = (type: "csv" | "xlsx") => {
    const token = getAuthToken();
    window.open(`/api/attendance/export/${type}?token=${token}`, "_blank");
  };

  // Jump from history to day sheet
  const handleJumpToSheet = (session: any) => {
    setSelectedBoard(String(session.board_id));
    setSelectedClass(String(session.class_id));
    setSelectedSection(session.section);
    setSelectedDate(session.attendance_date);
    setActiveSubTab("mark");
  };

  // Calculate live sheet metrics
  const presentCount = sheetStudents.filter((s) => s.status === "present").length;
  const absentCount = sheetStudents.length - presentCount;
  const presentPct =
    sheetStudents.length > 0 ? Math.round((presentCount / sheetStudents.length) * 1000) / 10 : 0;

  return (
    <div className="space-y-6">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#0F2C59] to-[#1E3A8A] text-white p-6 rounded-xl shadow-md border-l-4 border-amber-500">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center space-x-3.5">
            <div className="p-3 bg-amber-500 text-slate-950 rounded-xl shadow-md">
              <CalendarCheck className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <span className="bg-amber-400/20 text-amber-300 border border-amber-400/40 text-[10px] font-bold px-2 py-0.5 rounded font-mono uppercase">
                  Phase 5 Module
                </span>
                <span className="text-xs text-slate-300 font-semibold">
                  District School Education Department, Nuh
                </span>
              </div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight mt-0.5">
                Student Attendance Management & Analysis
              </h1>
              <p className="text-xs text-slate-200 mt-1">
                Daily Register • Bulk Entry • Class & Monthly Analysis • Low Attendance Alert Engine (&lt;75%)
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 shrink-0">
            <button
              onClick={() => handleExport("csv")}
              className="bg-white/10 hover:bg-white/20 text-white text-xs font-semibold px-3 py-2 rounded-lg border border-white/20 flex items-center space-x-1.5 transition"
            >
              <Download className="w-3.5 h-3.5 text-amber-400" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={() => handleExport("xlsx")}
              className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-lg shadow-sm flex items-center space-x-1.5 transition"
            >
              <FileSpreadsheet className="w-3.5 h-3.5" />
              <span>Export Excel</span>
            </button>
            <button
              onClick={() => {
                fetchSummary();
                if (activeSubTab === "mark") fetchSheet();
                if (activeSubTab === "history") fetchHistory();
                if (activeSubTab === "class-analysis") fetchClassAnalysis();
                if (activeSubTab === "monthly-analysis") fetchMonthlyAnalysis();
                if (activeSubTab === "low-attendance") fetchLowAttendance();
              }}
              disabled={loading}
              className="p-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20 transition disabled:opacity-50"
              title="Refresh Data"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard KPI Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {/* Total Students */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="text-[11px] font-bold text-slate-500 uppercase">Total Students</div>
          <div className="text-2xl font-black text-slate-900 mt-1 font-mono">
            {summary?.total_students || 0}
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Active Enrolled</div>
        </div>

        {/* Working Days */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="text-[11px] font-bold text-slate-500 uppercase">Working Days</div>
          <div className="text-2xl font-black text-blue-900 mt-1 font-mono">
            {summary?.total_working_days || 0}
          </div>
          <div className="text-[10px] text-blue-600 font-semibold mt-1">Marked Sessions</div>
        </div>

        {/* Average Attendance % */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="text-[11px] font-bold text-slate-500 uppercase">Overall Avg %</div>
          <div className="text-2xl font-black text-[#0F2C59] mt-1 font-mono">
            {summary?.average_attendance_percentage || 0}%
          </div>
          <div className="text-[10px] text-slate-400 mt-1">Cumulative Record</div>
        </div>

        {/* Today's Attendance % */}
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-xs">
          <div className="text-[11px] font-bold text-slate-500 uppercase">Today's %</div>
          <div className="text-2xl font-black text-emerald-700 mt-1 font-mono">
            {summary?.today_attendance_percentage || 0}%
          </div>
          <div className="text-[10px] text-emerald-600 font-semibold mt-1">
            {summary?.today_present || 0} Pres / {summary?.today_absent || 0} Abs
          </div>
        </div>

        {/* Below 75% Alert */}
        <div className="bg-amber-50/70 p-4 rounded-xl border border-amber-200 shadow-xs">
          <div className="text-[11px] font-bold text-amber-800 uppercase">Below 75%</div>
          <div className="text-2xl font-black text-amber-700 mt-1 font-mono">
            {summary?.students_below_75 || 0}
          </div>
          <div className="text-[10px] text-amber-600 font-semibold mt-1">Low Attendance Alert</div>
        </div>

        {/* Below 50% Critical Alert */}
        <div className="bg-rose-50/70 p-4 rounded-xl border border-rose-200 shadow-xs">
          <div className="text-[11px] font-bold text-rose-800 uppercase">Below 50%</div>
          <div className="text-2xl font-black text-rose-700 mt-1 font-mono">
            {summary?.students_below_50 || 0}
          </div>
          <div className="text-[10px] text-rose-600 font-semibold mt-1">Critical Board Alert</div>
        </div>
      </div>

      {/* Navigation Sub-Tabs */}
      <div className="flex border-b border-slate-200 overflow-x-auto bg-white rounded-t-xl px-2 pt-2 shadow-2xs">
        <button
          onClick={() => setActiveSubTab("mark")}
          className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition flex items-center space-x-2 ${
            activeSubTab === "mark"
              ? "border-[#0F2C59] text-[#0F2C59] bg-slate-50/50"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <CalendarCheck className="w-4 h-4" />
          <span>1. Mark / Edit Attendance</span>
        </button>

        <button
          onClick={() => setActiveSubTab("history")}
          className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition flex items-center space-x-2 ${
            activeSubTab === "history"
              ? "border-[#0F2C59] text-[#0F2C59] bg-slate-50/50"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Clock className="w-4 h-4" />
          <span>2. Attendance History</span>
        </button>

        <button
          onClick={() => setActiveSubTab("class-analysis")}
          className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition flex items-center space-x-2 ${
            activeSubTab === "class-analysis"
              ? "border-[#0F2C59] text-[#0F2C59] bg-slate-50/50"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Layers className="w-4 h-4" />
          <span>3. Class Attendance Analysis</span>
        </button>

        <button
          onClick={() => setActiveSubTab("monthly-analysis")}
          className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition flex items-center space-x-2 ${
            activeSubTab === "monthly-analysis"
              ? "border-[#0F2C59] text-[#0F2C59] bg-slate-50/50"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Calendar className="w-4 h-4 text-amber-600" />
          <span>4. Monthly Analysis</span>
        </button>

        <button
          onClick={() => setActiveSubTab("low-attendance")}
          className={`py-3 px-4 text-xs font-bold border-b-2 whitespace-nowrap transition flex items-center space-x-2 ${
            activeSubTab === "low-attendance"
              ? "border-rose-600 text-rose-700 bg-rose-50/50"
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <AlertTriangle className="w-4 h-4 text-rose-600" />
          <span>5. Low Attendance Students</span>
          {summary?.students_below_75 > 0 && (
            <span className="ml-1 px-1.5 py-0.2 bg-rose-600 text-white rounded-full text-[10px] font-mono">
              {summary.students_below_75}
            </span>
          )}
        </button>
      </div>

      {/* SUB-VIEW 1: MARK / EDIT ATTENDANCE */}
      {activeSubTab === "mark" && (
        <div className="space-y-4">
          {/* Filter Bar for Class & Date Selection */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-4">
            <div className="flex items-center justify-between mb-3 border-b border-slate-100 pb-2">
              <div className="flex items-center space-x-2 text-xs font-bold text-slate-800 uppercase tracking-wider">
                <Filter className="w-4 h-4 text-[#0F2C59]" />
                <span>Class & Date Selection</span>
              </div>
              {isRecorded && (
                <span className="bg-amber-100 text-amber-900 border border-amber-300 text-[11px] font-bold px-2.5 py-0.5 rounded flex items-center space-x-1">
                  <CheckCircle2 className="w-3.5 h-3.5 text-amber-700" />
                  <span>Attendance Already Recorded (Edit Mode)</span>
                </span>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
              {/* Board */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Board *
                </label>
                <select
                  value={selectedBoard}
                  onChange={(e) => setSelectedBoard(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg text-xs py-1.5 px-2.5 font-medium focus:ring-1 focus:ring-blue-500"
                >
                  {(boards.length > 0 ? boards : defaultBoards).map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.short_name} ({b.name})
                    </option>
                  ))}
                </select>
              </div>

              {/* Class */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Class *
                </label>
                <select
                  value={selectedClass}
                  onChange={(e) => setSelectedClass(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg text-xs py-1.5 px-2.5 font-medium focus:ring-1 focus:ring-blue-500"
                >
                  {(classes.length > 0 ? classes : defaultClasses).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Section */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Section *
                </label>
                <select
                  value={selectedSection}
                  onChange={(e) => setSelectedSection(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 rounded-lg text-xs py-1.5 px-2.5 font-medium focus:ring-1 focus:ring-blue-500"
                >
                  <option value="A">Section A</option>
                  <option value="B">Section B</option>
                  <option value="C">Section C</option>
                </select>
              </div>

              {/* Date */}
              <div>
                <label className="block text-[11px] font-bold text-slate-700 uppercase mb-1">
                  Attendance Date *
                </label>
                <div className="flex items-center space-x-1.5">
                  <input
                    type="date"
                    max={new Date().toISOString().split("T")[0]}
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    className="w-full bg-slate-50 border border-slate-300 rounded-lg text-xs py-1.5 px-2.5 font-medium focus:ring-1 focus:ring-blue-500"
                  />
                  <button
                    type="button"
                    onClick={() => setSelectedDate(new Date().toISOString().split("T")[0])}
                    className="text-[10px] bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold px-2 py-1.5 rounded transition"
                  >
                    Today
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Feedback Messages */}
          {saveSuccessMsg && (
            <div className="p-3 bg-emerald-50 border border-emerald-300 text-emerald-900 rounded-lg text-xs font-semibold flex items-center space-x-2 animate-in fade-in">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>{saveSuccessMsg}</span>
            </div>
          )}
          {saveErrorMsg && (
            <div className="p-3 bg-rose-50 border border-rose-300 text-rose-900 rounded-lg text-xs font-semibold flex items-center space-x-2 animate-in fade-in">
              <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{saveErrorMsg}</span>
            </div>
          )}

          {/* Attendance Sheet Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            {/* Sheet Control Bar */}
            <div className="p-4 bg-slate-50/80 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Users className="w-4 h-4 text-[#0F2C59]" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    Student Register ({sheetStudents.length} Students)
                  </span>
                </div>
                <div className="flex items-center space-x-1.5 text-xs">
                  <span className="bg-emerald-100 text-emerald-900 px-2 py-0.5 rounded font-mono font-bold">
                    {presentCount} Present
                  </span>
                  <span className="bg-rose-100 text-rose-900 px-2 py-0.5 rounded font-mono font-bold">
                    {absentCount} Absent
                  </span>
                  <span className="bg-blue-100 text-blue-900 px-2 py-0.5 rounded font-mono font-bold">
                    {presentPct}% Attendance
                  </span>
                </div>
              </div>

              {/* Bulk Quick Actions */}
              <div className="flex items-center space-x-2">
                <span className="text-[11px] font-bold text-slate-500 uppercase mr-1">Bulk:</span>
                <button
                  onClick={() => handleBulkStatus("present")}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg transition flex items-center space-x-1"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Present All</span>
                </button>
                <button
                  onClick={() => handleBulkStatus("absent")}
                  className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-2.5 py-1.5 rounded-lg transition flex items-center space-x-1"
                >
                  <X className="w-3.5 h-3.5" />
                  <span>Absent All</span>
                </button>
              </div>
            </div>

            {/* Student Table */}
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                    <th className="py-2.5 px-3">Roll No</th>
                    <th className="py-2.5 px-3">SRN</th>
                    <th className="py-2.5 px-3">Student Name</th>
                    <th className="py-2.5 px-3">Father's Name</th>
                    <th className="py-2.5 px-3">Board & Class</th>
                    <th className="py-2.5 px-3 text-center">Attendance Status</th>
                    <th className="py-2.5 px-3">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {sheetStudents.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-slate-500 italic">
                        {loading
                          ? "Loading class attendance sheet..."
                          : "No students enrolled for selected Board, Class, and Section."}
                      </td>
                    </tr>
                  ) : (
                    sheetStudents.map((s) => (
                      <tr
                        key={s.student_id}
                        className={`hover:bg-slate-50 transition ${
                          s.status === "absent" ? "bg-rose-50/20" : ""
                        }`}
                      >
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-700">
                          {s.roll_number || "—"}
                        </td>
                        <td className="py-2.5 px-3 font-mono font-bold text-slate-900">{s.srn}</td>
                        <td className="py-2.5 px-3">
                          <button
                            onClick={() => {
                              setSelectedStudentForProfile(s);
                              setIsProfileModalOpen(true);
                            }}
                            className="font-bold text-blue-900 hover:underline text-left"
                          >
                            {s.student_name}
                          </button>
                        </td>
                        <td className="py-2.5 px-3 text-slate-600">{s.father_name || "—"}</td>
                        <td className="py-2.5 px-3">
                          <span className="text-[10px] font-bold px-1.5 py-0.2 rounded bg-slate-100 text-slate-800 mr-1">
                            {s.board_code}
                          </span>
                          <span>
                            {s.class_name} - Sec {s.section}
                          </span>
                        </td>
                        <td className="py-2.5 px-3 text-center">
                          <div className="inline-flex items-center p-0.5 bg-slate-200 rounded-lg space-x-1">
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(s.student_id, "present")}
                              className={`px-3 py-1 rounded-md text-xs font-bold transition flex items-center space-x-1 ${
                                s.status === "present"
                                  ? "bg-emerald-600 text-white shadow-xs"
                                  : "text-slate-600 hover:text-slate-900"
                              }`}
                            >
                              <Check className="w-3 h-3" />
                              <span>Present</span>
                            </button>
                            <button
                              type="button"
                              onClick={() => handleToggleStatus(s.student_id, "absent")}
                              className={`px-3 py-1 rounded-md text-xs font-bold transition flex items-center space-x-1 ${
                                s.status === "absent"
                                  ? "bg-rose-600 text-white shadow-xs"
                                  : "text-slate-600 hover:text-slate-900"
                              }`}
                            >
                              <X className="w-3 h-3" />
                              <span>Absent</span>
                            </button>
                          </div>
                        </td>
                        <td className="py-2.5 px-3">
                          <input
                            type="text"
                            placeholder="Optional note..."
                            value={s.remarks || ""}
                            onChange={(e) => {
                              const val = e.target.value;
                              setSheetStudents((prev) =>
                                prev.map((item) =>
                                  item.student_id === s.student_id ? { ...item, remarks: val } : item
                                )
                              );
                            }}
                            className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 w-full max-w-xs focus:ring-1 focus:ring-blue-500"
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Footer Save Action Bar */}
            {sheetStudents.length > 0 && (
              <div className="p-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-3">
                <div className="text-xs text-slate-600">
                  Total Students: <strong className="font-mono">{sheetStudents.length}</strong> • Present:{" "}
                  <strong className="font-mono text-emerald-700">{presentCount}</strong> • Absent:{" "}
                  <strong className="font-mono text-rose-700">{absentCount}</strong>
                </div>
                <button
                  onClick={handleSaveAttendance}
                  disabled={savingAttendance}
                  className="bg-[#0F2C59] hover:bg-[#1E3A8A] text-white text-xs font-bold px-6 py-2.5 rounded-lg shadow-md flex items-center space-x-2 transition disabled:opacity-50"
                >
                  <Save className="w-4 h-4 text-amber-400" />
                  <span>{savingAttendance ? "Saving..." : isRecorded ? "Update Attendance" : "Save Attendance"}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* SUB-VIEW 2: ATTENDANCE HISTORY */}
      {activeSubTab === "history" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden space-y-3 p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-100 pb-3">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-2">
              <Clock className="w-4 h-4 text-[#0F2C59]" />
              <span>Attendance History Registry ({history.length} Recorded Sessions)</span>
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={historyFilterDate}
                onChange={(e) => setHistoryFilterDate(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-300 rounded-lg px-2 py-1"
              />
              <select
                value={historyFilterBoard}
                onChange={(e) => setHistoryFilterBoard(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-300 rounded-lg px-2 py-1"
              >
                <option value="">All Boards</option>
                {(boards.length > 0 ? boards : defaultBoards).map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.short_name}
                  </option>
                ))}
              </select>
              <select
                value={historyFilterClass}
                onChange={(e) => setHistoryFilterClass(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-300 rounded-lg px-2 py-1"
              >
                <option value="">All Classes</option>
                {(classes.length > 0 ? classes : defaultClasses).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  setHistoryFilterDate("");
                  setHistoryFilterBoard("");
                  setHistoryFilterClass("");
                  setHistoryFilterSection("");
                }}
                className="text-[11px] text-blue-700 hover:underline font-semibold"
              >
                Clear
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                  <th className="py-2.5 px-3">Date</th>
                  <th className="py-2.5 px-3">Board</th>
                  <th className="py-2.5 px-3">Class</th>
                  <th className="py-2.5 px-3">Section</th>
                  <th className="py-2.5 px-3 text-center">Total Students</th>
                  <th className="py-2.5 px-3 text-center">Present</th>
                  <th className="py-2.5 px-3 text-center">Absent</th>
                  <th className="py-2.5 px-3 text-right">Attendance %</th>
                  <th className="py-2.5 px-3">Recorded By</th>
                  <th className="py-2.5 px-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {history.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="py-8 text-center text-slate-500 italic">
                      No attendance history found for the selected filters.
                    </td>
                  </tr>
                ) : (
                  history.map((h, i) => (
                    <tr key={h.session_id || i} className="hover:bg-slate-50 transition">
                      <td className="py-2.5 px-3 font-mono font-bold text-slate-900">
                        {h.attendance_date}
                      </td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${
                            h.board_code === "HBSE"
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-blue-50 text-blue-800 border-blue-200"
                          }`}
                        >
                          {h.board_code}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 font-semibold text-slate-900">{h.class_name}</td>
                      <td className="py-2.5 px-3">Sec {h.section}</td>
                      <td className="py-2.5 px-3 text-center font-mono">{h.total_students}</td>
                      <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-700">
                        {h.present_count}
                      </td>
                      <td className="py-2.5 px-3 text-center font-mono font-bold text-rose-700">
                        {h.absent_count}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-[#0F2C59]">
                        {h.attendance_percentage}%
                      </td>
                      <td className="py-2.5 px-3 text-slate-600 text-[11px]">
                        {h.created_by_name || "Admin"}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => handleJumpToSheet(h)}
                          className="bg-[#0F2C59] hover:bg-[#1E3A8A] text-white text-[11px] font-semibold px-2 py-1 rounded transition"
                        >
                          Edit / View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUB-VIEW 3: CLASS ATTENDANCE ANALYSIS */}
      {activeSubTab === "class-analysis" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden space-y-3 p-4">
          <div className="border-b border-slate-100 pb-3">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center space-x-2">
              <Layers className="w-4 h-4 text-[#0F2C59]" />
              <span>Class & Section Attendance Comparative Analysis</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Aggregate attendance benchmarks across all active Classes, Boards, and Sections
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-slate-100/90 text-slate-700 font-bold border-b border-slate-200">
                  <th className="py-2.5 px-3">Board</th>
                  <th className="py-2.5 px-3">Class</th>
                  <th className="py-2.5 px-3">Section</th>
                  <th className="py-2.5 px-3 text-center">Enrolled</th>
                  <th className="py-2.5 px-3 text-center">Working Days</th>
                  <th className="py-2.5 px-3 text-center">Total Present</th>
                  <th className="py-2.5 px-3 text-center">Total Absent</th>
                  <th className="py-2.5 px-3 text-right">Average %</th>
                  <th className="py-2.5 px-3">Performance Bar</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {classAnalysis.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-50 transition">
                    <td className="py-2.5 px-3">
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${
                          c.board_code === "HBSE"
                            ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                            : "bg-blue-50 text-blue-800 border-blue-200"
                        }`}
                      >
                        {c.board_code}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 font-bold text-slate-900">{c.class_name}</td>
                    <td className="py-2.5 px-3">
                      <span className="bg-slate-100 text-slate-800 font-bold text-xs px-2 py-0.5 rounded">
                        Sec {c.section}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono">{c.total_students}</td>
                    <td className="py-2.5 px-3 text-center font-mono font-bold text-blue-900">
                      {c.working_days}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-emerald-700 font-bold">
                      {c.total_present}
                    </td>
                    <td className="py-2.5 px-3 text-center font-mono text-rose-700 font-bold">
                      {c.total_absent}
                    </td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-[#0F2C59]">
                      {c.average_attendance_percentage}%
                    </td>
                    <td className="py-2.5 px-3 w-44">
                      <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
                        <div
                          className={`h-2 rounded-full ${
                            c.average_attendance_percentage >= 75
                              ? "bg-emerald-600"
                              : c.average_attendance_percentage >= 50
                              ? "bg-amber-500"
                              : "bg-rose-600"
                          }`}
                          style={{ width: `${Math.min(100, c.average_attendance_percentage)}%` }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* SUB-VIEW 4: MONTHLY ATTENDANCE ANALYSIS */}
      {activeSubTab === "monthly-analysis" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden space-y-3 p-4">
          <div className="border-b border-slate-100 pb-3">
            <h3 className="text-xs font-bold text-amber-900 uppercase tracking-wider flex items-center space-x-2">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span>Academic Session Monthly Attendance Analysis (April to March)</span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Tracks month-by-month working days, attendance percentage, and cumulative student engagement
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            {monthlyAnalysis.map((m, i) => (
              <div
                key={m.month_number || i}
                className="p-4 rounded-lg border border-slate-200 bg-slate-50/70 hover:bg-white hover:border-[#0F2C59] transition"
              >
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xs font-bold text-slate-900">{m.month_name} {m.year}</span>
                  <span
                    className={`text-[10px] font-mono font-bold px-2 py-0.5 rounded ${
                      m.attendance_percentage >= 75
                        ? "bg-emerald-100 text-emerald-800"
                        : m.attendance_percentage >= 50
                        ? "bg-amber-100 text-amber-800"
                        : "bg-rose-100 text-rose-800"
                    }`}
                  >
                    {m.attendance_percentage}%
                  </span>
                </div>
                <div className="py-2 border-t border-slate-200 flex justify-between items-center text-xs">
                  <span className="text-slate-600">Working Days:</span>
                  <span className="font-mono font-bold text-slate-900">{m.working_days} Days</span>
                </div>
                <div className="flex justify-between items-center pt-1 text-xs">
                  <span className="text-slate-600">Total Present:</span>
                  <span className="font-mono font-semibold text-emerald-700">{m.total_present}</span>
                </div>
                <div className="flex justify-between items-center pt-1 text-xs">
                  <span className="text-slate-600">Total Absent:</span>
                  <span className="font-mono font-semibold text-rose-700">{m.total_absent}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* SUB-VIEW 5: LOW ATTENDANCE STUDENTS */}
      {activeSubTab === "low-attendance" && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden space-y-4 p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 border-b border-slate-100 pb-3">
            <div>
              <h3 className="text-xs font-bold text-rose-900 uppercase tracking-wider flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-rose-600" />
                <span>Low Attendance Engine (&lt;{lowThreshold}% Threshold)</span>
              </h3>
              <p className="text-[11px] text-slate-500 mt-0.5">
                Students below 75% attendance threshold requiring district parent contact and attendance counseling
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-xs font-semibold text-slate-600">Threshold:</span>
              <select
                value={lowThreshold}
                onChange={(e) => setLowThreshold(Number(e.target.value))}
                className="text-xs bg-slate-50 border border-slate-300 rounded px-2 py-1 font-bold text-slate-800"
              >
                <option value={50}>Below 50% (Critical)</option>
                <option value={60}>Below 60% (Very Low)</option>
                <option value={75}>Below 75% (Standard Default)</option>
              </select>

              <select
                value={lowPriorityFilter}
                onChange={(e) => setLowPriorityFilter(e.target.value)}
                className="text-xs bg-slate-50 border border-slate-300 rounded px-2 py-1 font-bold text-slate-800"
              >
                <option value="ALL">All Priorities</option>
                <option value="CRITICAL">Critical Only</option>
                <option value="VERY LOW">Very Low Only</option>
                <option value="LOW">Low Only</option>
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-rose-50/80 text-rose-950 font-bold border-b border-rose-200">
                  <th className="py-2.5 px-3">SRN</th>
                  <th className="py-2.5 px-3">Student Name</th>
                  <th className="py-2.5 px-3">Board</th>
                  <th className="py-2.5 px-3">Class & Section</th>
                  <th className="py-2.5 px-3 text-center">Working Days</th>
                  <th className="py-2.5 px-3 text-center">Present</th>
                  <th className="py-2.5 px-3 text-center">Absent</th>
                  <th className="py-2.5 px-3 text-right">Attendance %</th>
                  <th className="py-2.5 px-3 text-center">Priority</th>
                  <th className="py-2.5 px-3 text-right">Deficit from 75%</th>
                  <th className="py-2.5 px-3 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {lowAttendanceList.length === 0 ? (
                  <tr>
                    <td colSpan={11} className="py-8 text-center text-slate-500 italic">
                      No students found below the selected attendance threshold.
                    </td>
                  </tr>
                ) : (
                  lowAttendanceList.map((st, i) => (
                    <tr key={st.id || i} className="hover:bg-rose-50/30 transition">
                      <td className="py-2.5 px-3 font-mono font-bold text-slate-900">{st.srn}</td>
                      <td className="py-2.5 px-3 font-bold text-slate-900">{st.student_name}</td>
                      <td className="py-2.5 px-3">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase ${
                            st.board_code === "HBSE"
                              ? "bg-emerald-50 text-emerald-800 border-emerald-200"
                              : "bg-blue-50 text-blue-800 border-blue-200"
                          }`}
                        >
                          {st.board_code}
                        </span>
                      </td>
                      <td className="py-2.5 px-3">
                        {st.class_name} - Sec {st.section}
                      </td>
                      <td className="py-2.5 px-3 text-center font-mono">{st.total_working_days}</td>
                      <td className="py-2.5 px-3 text-center font-mono font-bold text-emerald-700">
                        {st.present_days}
                      </td>
                      <td className="py-2.5 px-3 text-center font-mono font-bold text-rose-700">
                        {st.absent_days}
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-700">
                        {st.attendance_percentage}%
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${
                            st.priority === "Critical"
                              ? "bg-rose-600 text-white"
                              : st.priority === "Very Low"
                              ? "bg-rose-100 text-rose-900 border border-rose-300"
                              : "bg-amber-100 text-amber-900 border border-amber-300"
                          }`}
                        >
                          {st.priority}
                        </span>
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono text-rose-800 font-bold">
                        -{st.deficit_percentage}%
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => {
                            setSelectedStudentForProfile(st);
                            setIsProfileModalOpen(true);
                          }}
                          className="bg-[#0F2C59] hover:bg-[#1E3A8A] text-white text-[11px] font-semibold px-2 py-1 rounded transition"
                        >
                          View Profile
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Student Profile Modal with Attendance Profile */}
      {isProfileModalOpen && (
        <StudentProfileModal
          isOpen={isProfileModalOpen}
          student={selectedStudentForProfile}
          onClose={() => {
            setIsProfileModalOpen(false);
            setSelectedStudentForProfile(null);
          }}
        />
      )}
    </div>
  );
};
